<template>
  <div>
    <b-form-group
      v-if="showInternalLabel"
      :label="label"
      :label-cols-lg="labelColsLg"
      :label-class="labelClass"
      :class="required ? 'required' : ''"
    >
      <div
        class="location-picker-trigger"
        :class="{ 'disabled': disabled }"
        role="button"
        :tabindex="disabled ? -1 : 0"
        data-test="location-picker-trigger"
        @click="!disabled && openModal()"
        @keyup.enter="!disabled && openModal()"
      >
        <feather-icon
          icon="MapPinIcon"
          class="mr-50"
        />
        <span
          v-if="displayText"
          class="flex-text"
        >{{ displayText }}</span>
        <span
          v-else
          class="text-muted"
        >Select Location
        </span>
        <span
          v-if="clearable && value"
          class="ml-auto clear-control"
          role="button"
          aria-label="Clear location"
          data-test="location-clear"
          tabindex="0"
          @click.stop="clearLocation"
          @keyup.enter.stop="clearLocation"
        >
          <feather-icon icon="XIcon" />
        </span>
      </div>

      <b-modal
        :id="modalId"
        title="Select Location"
        hide-footer
        size="lg"
        @shown="onModalShown"
        @hidden="onModalHidden"
      >
        <p class="text-muted mb-2">
          Select Locations using GPS, search, WhatsApp link, or select on the map
        </p>

        <b-tabs
          v-model="activeTab"
          content-class="mt-2"
        >
          <b-tab
            title="GPS"
            active
          >
            <div class="d-flex justify-content-center my-1">
              <b-button
                variant="primary"
                data-test="gps-set-location"
                @click="setLocationFromGPS"
              >Set Location</b-button>
            </div>
          </b-tab>
          <b-tab title="Search">
            <div class="place-autocomplete-wrapper">
              <input
                id="place-autocomplete-input"
                ref="searchInput"
                type="text"
                placeholder="Search for an address..."
                class="form-control"
              >
              <div
                ref="placeAutocompleteContainer"
                class="place-autocomplete-container"
              />
            </div>
            <b-button
              variant="primary"
              class="mt-1"
              data-test="search-confirm-location"
              @click="confirmSelection"
            >Set Location</b-button>
          </b-tab>
          <b-tab title="Link">
            <b-form-group label="Google Maps link">
              <b-form-input
                v-model="linkInput"
                placeholder="Paste a Google Maps shareable link (supports short links)"
              />
            </b-form-group>
            <b-button
              variant="primary"
              data-test="link-set-location"
              @click="setLocationFromLink"
            >Set ocation</b-button>
          </b-tab>
          <b-tab
            title="Map"
            @activated="onMapTabActivated"
          >
            <div
              ref="map"
              style="width: 100%; height: 320px; border-radius: 6px; overflow: hidden;"
            />
            <b-button
              variant="primary"
              class="mt-1"
              data-test="map-confirm-location"
              @click="confirmSelection"
            >Set Location</b-button>
          </b-tab>
        </b-tabs>
      </b-modal>
    </b-form-group>
    <div v-else>
      <div
        class="location-picker-trigger"
        :class="{ 'disabled': disabled }"
        role="button"
        :tabindex="disabled ? -1 : 0"
        data-test="location-picker-trigger"
        @click="!disabled && openModal()"
        @keyup.enter="!disabled && openModal()"
      >
        <feather-icon
          icon="MapPinIcon"
          class="mr-50"
        />
        <span
          v-if="displayText"
          class="flex-text"
        >{{ displayText }}</span>
        <span
          v-else
          class="text-muted"
        >Select Location
        </span>
        <span
          v-if="clearable && value"
          class="ml-auto clear-control"
          role="button"
          aria-label="Clear location"
          data-test="location-clear"
          tabindex="0"
          @click.stop="clearLocation"
          @keyup.enter.stop="clearLocation"
        >
          <feather-icon icon="XIcon" />
        </span>
      </div>

      <b-modal
        :id="modalId"
        title="Select Location"
        hide-footer
        size="lg"
        @shown="onModalShown"
        @hidden="onModalHidden"
      >
        <p class="text-muted mb-2">
          Select Locations using GPS, search, WhatsApp link, or select on the map
        </p>

        <b-tabs
          v-model="activeTab"
          content-class="mt-2"
        >
          <b-tab
            title="GPS"
            active
          >
            <div class="d-flex justify-content-center my-1">
              <b-button
                variant="primary"
                data-test="gps-set-location"
                @click="setLocationFromGPS"
              >Set Location</b-button>
            </div>
          </b-tab>
          <b-tab title="Search">
            <div class="place-autocomplete-wrapper">
              <input
                id="place-autocomplete-input"
                ref="searchInput"
                type="text"
                placeholder="Search for an address..."
                class="form-control"
              >
              <div
                ref="placeAutocompleteContainer"
                class="place-autocomplete-container"
              />
            </div>
            <b-button
              variant="primary"
              class="mt-1"
              data-test="search-confirm-location"
              @click="confirmSelection"
            >Set Location</b-button>
          </b-tab>
          <b-tab title="Link">
            <b-form-group label="Google Maps link">
              <b-form-input
                v-model="linkInput"
                placeholder="Paste a Google Maps shareable link (supports short links)"
              />
            </b-form-group>
            <b-button
              variant="primary"
              class="mt-1"
              data-test="link-confirm-location"
              @click="setLocationFromLink"
            >Set Location</b-button>
          </b-tab>
          <b-tab
            title="Map"
            @activated="onMapTabActivated"
          >
            <div
              ref="mapContainer"
              class="map-container"
              style="height: 400px;"
            />
            <b-button
              variant="primary"
              class="mt-1"
              data-test="map-confirm-location"
              @click="confirmFromMap"
            >Set Location</b-button>
          </b-tab>
        </b-tabs>
      </b-modal>
    </div>
  </div>
</template>

<script>
import {
  BFormGroup, BModal, BTabs, BTab,
  BFormInput, BButton,
} from 'bootstrap-vue'
import ErrorMessage from '@core/mixins/ErrorMessage'

export default {
  name: 'BaseLocationPicker',
  components: {
    BFormGroup,
    BModal,
    BTabs,
    BTab,
    BFormInput,
    BButton,
  },
  mixins: [ErrorMessage],
  props: {
    value: {
      type: [Object, String, null],
      default: null,
    },
    label: {
      type: String,
      default: 'Select Location',
    },
    labelClass: {
      type: [String, Array, Object],
      // No default so it uses the standard form-group label color
      default: null,
    },
    labelColsLg: {
      type: [String, Number],
      default: undefined,
    },
    required: {
      type: Boolean,
      default: false,
    },
    placeholderText: {
      type: String,
      default: 'Select Location',
    },
    modalId: {
      type: String,
      default: () => `location-modal-${Math.random().toString(36).substr(2, 9)}`,
    },
    googleApiKey: {
      type: String,
      default: process.env.VUE_APP_GOOGLE_MAPS_API_KEY || '',
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    showInternalLabel: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchQuery: '',
      map: null,
      marker: null,
      geocoder: null,
      placeAutocompleteElement: null,
      activeTab: 0,
      linkInput: '',
      resolvingShortLink: false,
      pendingSelection: null,
    }
  },
  computed: {
    displayText() {
      if (!this.value) return ''
      if (typeof this.value === 'string') return this.value
      if (typeof this.value === 'object') {
        const address = this.value.address || ''
        const lat = this.value.latitude
        const lng = this.value.longitude
        const hasCoords = typeof lat === 'number' && typeof lng === 'number'
        if (address && hasCoords) return `${address} (${lat}, ${lng})`
        if (address) return address
        if (hasCoords) return `${lat}, ${lng}`
      }
      return ''
    },
    gpsDisplay() {
      if (!this.value) return ''
      if (typeof this.value === 'object' && this.value.source === 'gps') {
        const lat = this.value.latitude
        const lng = this.value.longitude
        const addr = this.value.address || ''
        return addr ? `${addr} (${lat}, ${lng})` : `${lat}, ${lng}`
      }
      return ''
    },
  },
  methods: {
    openModal() {
      this.$bvModal.show(this.modalId)
      this.$nextTick(() => {
        this.bootstrapGoogleIfNeeded()
      })
    },
    closeModal() {
      this.$bvModal.hide(this.modalId)
    },
    onMapTabActivated() {
      // Called when user switches to Map tab
      if (window.google && window.google.maps && window.google.maps.places) {
        this.$nextTick(() => {
          this.ensureMap()
        })
      } else {
        this.bootstrapGoogleIfNeeded().then(() => {
          this.ensureMap()
        })
      }
    },
    async bootstrapGoogleIfNeeded() {
      if (window.google && window.google.maps && window.google.maps.places) {
        await this.initAutocomplete()
        if (!this.geocoder) this.geocoder = new window.google.maps.Geocoder()
        return
      }
      if (!this.googleApiKey) {
        this.showErrorMessage('Google Maps API key not found. Please contact support.')
        return
      }
      try {
        await this.loadGoogleMapsScript(this.googleApiKey)
        // Wait for window.google and places library to be ready
        await new Promise(resolve => {
          const check = () => (window.google && window.google.maps && window.google.maps.places ? resolve() : setTimeout(check, 50))
          check()
        })
        await this.initAutocomplete()
        if (!this.geocoder) this.geocoder = new window.google.maps.Geocoder()
      } catch (error) {
        this.convertAndNotifyError(error)
      }
    },
    loadGoogleMapsScript(apiKey) {
      return new Promise(resolve => {
        if (document.getElementById('gmaps-script')) return resolve()
        const script = document.createElement('script')
        script.id = 'gmaps-script'
        script.type = 'text/javascript'
        script.async = true
        script.defer = true
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`
        script.onload = () => resolve()
        document.head.appendChild(script)
        return null
      })
    },
    onModalShown() {
      // Ensure the map is initialized once the modal is visible
      if (window.google && window.google.maps) {
        this.$nextTick(() => this.ensureMap())
      }
    },
    onModalHidden() {
      // Reset dynamic elements so they reinitialize properly on next open
      try {
        if (this.$refs && this.$refs.placeAutocompleteContainer) {
          this.$refs.placeAutocompleteContainer.innerHTML = ''
        }
      } catch (e) {
        // Silently handle cleanup errors
      }
      this.placeAutocompleteElement = null
      this.map = null
      this.marker = null
      this.pendingSelection = null
    },
    ensureMap() {
      if (!window.google || !window.google.maps) {
        this.showErrorMessage('Google Maps API not loaded. Please refresh the page.')
        return
      }
      const mapRef = this.$refs.map || this.$refs.mapContainer
      if (!mapRef) {
        this.showErrorMessage('Map container not found. Please try again.')
        return
      }
      if (this.map) {
        window.google.maps.event.trigger(this.map, 'resize')
        return
      }
      const defaultCenter = { lat: 6.927079, lng: 79.861244 }
      try {
        this.map = new window.google.maps.Map(mapRef, {
          center: defaultCenter,
          zoom: 12,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
        })
        this.geocoder = new window.google.maps.Geocoder()
        this.map.addListener('click', e => {
          this.placeMarkerAndSelect(e.latLng)
        })
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(pos => {
            const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude }
            this.map.setCenter(loc)
          })
        }
      } catch (error) {
        this.convertAndNotifyError(error)
      }
    },
    async initAutocomplete() {
      if (!this.$refs.placeAutocompleteContainer || !this.$refs.searchInput || !window.google || !window.google.maps || !window.google.maps.places) return
      if (this.placeAutocompleteElement) return
      try {
        // Import the PlaceAutocompleteElement from the places library
        const { PlaceAutocompleteElement } = await window.google.maps.importLibrary('places')
        // Create the PlaceAutocompleteElement
        this.placeAutocompleteElement = new PlaceAutocompleteElement()
        // Set the id to match the input element
        this.placeAutocompleteElement.id = 'place-autocomplete-input'
        // Mirror placeholder and current text
        this.placeAutocompleteElement.placeholder = this.$refs.searchInput.getAttribute('placeholder') || 'Search for an address...'
        if (this.searchQuery) this.placeAutocompleteElement.value = this.searchQuery
        // Add it to the container
        if (this.$refs.placeAutocompleteContainer) this.$refs.placeAutocompleteContainer.innerHTML = ''
        this.$refs.placeAutocompleteContainer.appendChild(this.placeAutocompleteElement)
        // Leave default Google styles (no forced colors)
        // Hide the raw input once the Google element is rendered
        if (this.$refs && this.$refs.searchInput) this.$refs.searchInput.style.display = 'none'
        // Add event listener for place selection
        this.placeAutocompleteElement.addEventListener('gmp-select', async event => {
          try {
            const place = event.placePrediction.toPlace()
            await place.fetchFields({
              fields: ['displayName', 'formattedAddress', 'location'],
            })
            if (!place.location) return
            const loc = place.location
            this.searchQuery = place.formattedAddress || place.displayName
            this.emitSelection({
              address: place.formattedAddress || place.displayName,
              latitude: loc.lat(),
              longitude: loc.lng(),
              source: 'autocomplete',
            })
            if (this.map) {
              this.map.setCenter(loc)
              this.placeMarkerAndSelect(loc)
            } else {
              this.ensureMap()
            }
          } catch (error) {
            this.convertAndNotifyError(error)
          }
        })
      } catch (error) {
        this.convertAndNotifyError(error)
      }
    },
    selectFromSearch() {
      if (!this.searchQuery) return
      // If Google is available, geocode the query to precise coords
      if (window.google && window.google.maps) {
        if (!this.geocoder) this.geocoder = new window.google.maps.Geocoder()
        this.geocoder.geocode({ address: this.searchQuery }, (results, status) => {
          if (status === 'OK' && results && results[0]) {
            const result = results[0]
            const loc = result.geometry.location
            const payload = {
              address: result.formatted_address,
              latitude: loc.lat(),
              longitude: loc.lng(),
              source: 'search',
            }
            this.emitSelection(payload)
            if (this.map) {
              this.map.setCenter(loc)
              this.placeMarkerAndSelect(loc)
            }
            this.closeModal()
            return
          }
          // Fallback to plain text emit if geocoding fails
          const selected = {
            address: this.searchQuery,
            latitude: null,
            longitude: null,
            source: 'search',
          }
          this.$emit('input', selected)
          this.closeModal()
        })
        return
      }
      // Fallback when Google is not available
      const selected = {
        address: this.searchQuery,
        latitude: null,
        longitude: null,
        source: 'search',
      }
      this.$emit('input', selected)
      this.closeModal()
    },
    placeMarkerAndSelect(latLng) {
      if (!this.marker) {
        this.marker = new window.google.maps.Marker({ map: this.map })
      }
      this.marker.setPosition(latLng)
      if (this.geocoder) {
        this.geocoder.geocode({ location: latLng }, (results, status) => {
          const address = status === 'OK' && results && results[0] ? results[0].formatted_address : this.searchQuery
          this.emitSelection({
            address,
            latitude: typeof latLng.lat === 'function' ? latLng.lat() : latLng.lat,
            longitude: typeof latLng.lng === 'function' ? latLng.lng() : latLng.lng,
            source: 'map',
          })
        })
      } else {
        // If geocoder is not available, still emit the selection
        this.emitSelection({
          address: '',
          latitude: typeof latLng.lat === 'function' ? latLng.lat() : latLng.lat,
          longitude: typeof latLng.lng === 'function' ? latLng.lng() : latLng.lng,
          source: 'map',
        })
      }
    },
    emitSelection(payload) {
      this.pendingSelection = payload
      this.$emit('input', payload)
    },
    confirmSelection() {
      if (this.pendingSelection) this.closeModal()
    },
    confirmFromMap() {
      // Close modal when Set location button is clicked
      this.closeModal()
    },
    clearLocation() {
      this.pendingSelection = null
      this.$emit('input', null)
    },
    async setLocationFromLink() {
      if (!this.linkInput) return
      const rawInput = this.linkInput.trim()
      const cleaned = rawInput.replace(/^@/, '')

      const tryEmit = (lat, lng) => {
        const latitude = Number(lat)
        const longitude = Number(lng)
        if (Number.isNaN(latitude) || Number.isNaN(longitude)) return false
        const coords = { lat: latitude, lng: longitude }
        this.ensureMap()
        if (this.map) this.map.setCenter(coords)

        const finalize = address => {
          this.emitSelection(
            {
              address, latitude, longitude, source: 'link',
            },
          )
          if (this.map) this.placeMarkerAndSelect(coords)
          this.closeModal()
        }

        if (window.google && window.google.maps) {
          if (!this.geocoder) this.geocoder = new window.google.maps.Geocoder()
          this.geocoder.geocode({ location: coords }, (results, status) => {
            const address = status === 'OK' && results && results[0] ? results[0].formatted_address : ''
            finalize(address)
          })
        } else {
          finalize('')
        }
        return true
      }

      // Pattern 1: .../@lat,lng
      const atMatch = cleaned.match(/@(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/)
      if (atMatch && tryEmit(atMatch[1], atMatch[2])) return

      // If it's not a full URL, try direct "lat,lng"
      const direct = cleaned.match(/^\s*(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)\s*$/)
      if (direct && tryEmit(direct[1], direct[2])) return

      // Try parsing as URL and read query params
      try {
        const url = new URL(cleaned)
        const ll = url.searchParams.get('ll')
        if (ll) {
          const [latStr, lngStr] = ll.split(',')
          if (tryEmit(latStr, lngStr)) return
        }
        const q = url.searchParams.get('q')
        if (q) {
          const qCoords = q.match(/(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)/)
          if (qCoords && tryEmit(qCoords[1], qCoords[2])) return
          // Fallback: geocode address string in q
          if (window.google && window.google.maps) {
            if (!this.geocoder) this.geocoder = new window.google.maps.Geocoder()
            this.geocoder.geocode({ address: q }, (results, status) => {
              if (status === 'OK' && results && results[0]) {
                const loc = results[0].geometry.location
                tryEmit(loc.lat(), loc.lng())
              } else if (this.$bvToast) {
                this.showErrorMessage('Could not resolve the link to a location.')
              }
            })
            return
          }
        }

        // Attempt to resolve short links (maps.app.goo.gl) via a CORS-friendly HTML proxy
        if (/maps\.app\.goo\.gl$/.test(url.hostname)) {
          try {
            const proxyUrl = `https://r.jina.ai/http://${url.host}${url.pathname}${url.search}`
            const html = await fetch(proxyUrl).then(r => r.text())
            // Try to extract canonical Google Maps URL
            const canonicalMatch = html.match(/<link[^>]+rel=["']canonical["'][^>]+href=["']([^"']+)/i)
            const candidate = canonicalMatch && canonicalMatch[1] ? canonicalMatch[1] : null
            if (candidate) {
              // Re-run parsing on the resolved URL
              this.linkInput = candidate
              // Recursively call once; guard to avoid loops if it fails
              if (this.resolvingShortLink) return
              this.resolvingShortLink = true
              await this.setLocationFromLink()
              this.resolvingShortLink = false
              return
            }
            // Fallback: scan HTML text for an @lat,lng pattern
            const coordMatch = html.match(/@(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/)
            if (coordMatch && tryEmit(coordMatch[1], coordMatch[2])) return
          } catch (e) {
            // Silently handle short link resolution errors
          }
          this.$bvToast.toast('Short link detected. If location was not set, open the link once and paste the expanded Maps URL.', { variant: 'info' })
          return
        }
      } catch (e) {
        this.showErrorMessage('Invalid link format. Please paste a valid Google Maps link.')
      }
    },
    setLocationFromGPS() {
      if (!navigator.geolocation) {
        this.showErrorMessage('Geolocation not supported on this browser.')
        return
      }
      navigator.geolocation.getCurrentPosition(async pos => {
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude }
        // Ensure map exists and center it
        this.ensureMap()
        if (this.map) this.map.setCenter(coords)
        // Reverse geocode if possible
        const handleEmit = address => {
          this.emitSelection({
            address,
            latitude: coords.lat,
            longitude: coords.lng,
            source: 'gps',
          })
          if (this.map) this.placeMarkerAndSelect(coords)
          this.closeModal()
        }
        try {
          if (window.google && window.google.maps) {
            if (!this.geocoder) this.geocoder = new window.google.maps.Geocoder()
            this.geocoder.geocode({ location: coords }, (results, status) => {
              const address = status === 'OK' && results && results[0] ? results[0].formatted_address : ''
              handleEmit(address)
            })
          } else {
            handleEmit('')
          }
        } catch (e) {
          handleEmit('')
        }
      }, err => {
        const message = err && err.message ? err.message : 'Unable to get current location.'
        this.showErrorMessage(message)
      }, { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 })
    },
  },
}
</script>

<style lang="scss" scoped>
.location-picker-trigger {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.375rem 0.75rem;
  background: #EFEFEF;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  cursor: pointer;
  min-height: calc(1.9em + 0.75rem + 2px);
  font-size: 1rem;
  line-height: 1.5;
  color: #495057;
}
.location-picker-trigger:focus {
  border-color: #80bdff;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}
.location-picker-trigger.disabled {
  background-color: #f8f9fa !important;
  border-color: #e9ecef !important;
  color: #6c757d !important;
  cursor: not-allowed !important;
  opacity: 0.7 !important;
}
.flex-text {
  flex: 1 1 auto;
}
.clear-control {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
}
.clear-control:hover {
  color: #dc3545;
}
.gm-suggest-list {
  background: #fff;
  border: 1px solid #e3e5ea;
  border-radius: 6px;
  max-height: 220px;
  overflow-y: auto;
}
.gm-suggest-item {
  padding: 0.5rem 0.75rem;
  cursor: pointer;
}
.gm-suggest-item:hover {
  background: #f7f8fa;
}
.place-autocomplete-wrapper {
  position: relative;
  width: 100%;
}
.place-autocomplete-container {
  width: 100%;
}
.place-autocomplete-container gmp-place-autocomplete {
  width: 100%;
}
/* Style internal input via parts API used by Google web component */

#place-autocomplete-input {
  width: 100%;
  padding: 0.375rem 0.75rem;
  background: #fff;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #495057;
}
#place-autocomplete-input:focus {
  border-color: #80bdff;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}
</style>
